import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  GraduationCap, Plus, Power, RefreshCw, IndianRupee, Receipt,
  Loader2, Copy, X, Eye,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";

const DEFAULT_FORM = {
  name: "", email: "", password: "", phone: "",
  college: "", state: "", city: "", referral_code: "",
  notify_email: true,
};

export default function AdminAmbassadors() {
  const { formatApiError } = useAuth();
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState(DEFAULT_FORM);
  const [createOpen, setCreateOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [txnUser, setTxnUser] = useState(null);
  const [txns, setTxns] = useState([]);
  const [payoutAmt, setPayoutAmt] = useState("");
  const [payoutNote, setPayoutNote] = useState("");

  useEffect(() => { load(); }, []);
  const load = async () => {
    const r = await api.get("/admin/ambassadors");
    setItems(r.data || []);
  };

  const create = async () => {
    if (!createForm.name || !createForm.email || !createForm.password) {
      toast.error("Name, email and password are required");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/admin/ambassadors", createForm);
      toast.success("Ambassador created — referral code assigned");
      setCreateOpen(false);
      setCreateForm(DEFAULT_FORM);
      load();
    } catch (e) {
      toast.error(formatApiError?.(e.response?.data?.detail) || "Failed");
    } finally { setSubmitting(false); }
  };

  const toggleStatus = async (u) => {
    const next = (u.ambassador_status || "active") === "active" ? "inactive" : "active";
    try {
      await api.put(`/admin/ambassadors/${u.id}`, { ambassador_status: next });
      toast.success(`Marked ${next}`);
      load();
    } catch { toast.error("Failed"); }
  };

  const updateCode = async (u) => {
    const cur = u.referral_code || "";
    const next = window.prompt("New referral code (uppercase letters/digits, e.g. AMBSONAL):", cur);
    if (!next || next.toUpperCase() === cur.toUpperCase()) return;
    try {
      await api.put(`/admin/ambassadors/${u.id}`, { referral_code: next.toUpperCase() });
      toast.success("Referral code updated");
      load();
    } catch (e) {
      toast.error(formatApiError?.(e.response?.data?.detail) || "Failed");
    }
  };

  const openTxns = async (u) => {
    setTxnUser(u);
    setPayoutAmt(""); setPayoutNote("");
    const r = await api.get(`/admin/ambassadors/${u.id}/transactions`);
    setTxns(r.data || []);
  };

  const recordPayout = async () => {
    const amt = parseFloat(payoutAmt);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    try {
      await api.post(`/admin/ambassadors/${txnUser.id}/payout`, {
        amount: amt, note: payoutNote || "",
      });
      toast.success("Payout recorded — wallet debited");
      // refresh
      const r1 = await api.get(`/admin/ambassadors/${txnUser.id}/transactions`);
      setTxns(r1.data || []);
      load();
      setPayoutAmt(""); setPayoutNote("");
    } catch (e) {
      toast.error(formatApiError?.(e.response?.data?.detail) || "Payout failed");
    }
  };

  const copy = async (txt) => {
    try { await navigator.clipboard.writeText(txt); toast.success("Copied"); } catch {}
  };

  return (
    <DashboardLayout>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="heading text-2xl font-black inline-flex items-center gap-2">
            <GraduationCap size={22} /> College Ambassadors
          </h1>
          <p className="text-sm text-slate-500 mt-1">Each ambassador gets a unique referral code. Buyer gets 10% off, ambassador earns 10% on every paid test-series purchase.</p>
        </div>
        <button
          onClick={() => setCreateOpen(true)}
          className="bg-[#F59E0B] hover:bg-[#D97706] text-white font-black rounded-lg px-5 py-2.5 inline-flex items-center gap-2"
          data-testid="add-ambassador-btn"
        >
          <Plus size={16} /> Add Ambassador
        </button>
      </div>

      <div className="mt-6 grid lg:grid-cols-2 gap-5">
        {items.length === 0 && (
          <div className="lg:col-span-2 p-12 rounded-xl border border-dashed border-slate-300 text-center">
            <GraduationCap size={40} className="mx-auto text-slate-300" />
            <div className="mt-3 text-slate-500 font-semibold">No ambassadors yet.</div>
            <div className="text-xs text-slate-400 mt-1">Click "Add Ambassador" to onboard your first one.</div>
          </div>
        )}
        {items.map((u) => {
          const active = (u.ambassador_status || "active") === "active";
          return (
            <div key={u.id} className={`p-5 rounded-xl border-2 ${active ? "border-emerald-200 bg-emerald-50/30" : "border-slate-200 bg-white"}`} data-testid={`ambassador-card-${u.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="heading font-black text-lg truncate">{u.name}</div>
                    <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded ${active ? "bg-emerald-200 text-emerald-800" : "bg-slate-200 text-slate-600"}`} data-testid={`amb-status-${u.id}`}>
                      {active ? "Active" : "Inactive"}
                    </span>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">{u.email} · {u.phone || "No phone"}</div>
                  <div className="text-xs text-slate-500">{u.college || "—"}{u.city ? `, ${u.city}` : ""}</div>
                </div>
                <button
                  onClick={() => toggleStatus(u)}
                  className={`p-2 rounded ${active ? "text-amber-700 hover:bg-amber-50" : "text-emerald-700 hover:bg-emerald-50"}`}
                  title={active ? "Set inactive" : "Set active"}
                  data-testid={`amb-toggle-${u.id}`}
                >
                  <Power size={16} />
                </button>
              </div>

              {/* Referral code & wallet metrics */}
              <div className="mt-4 grid grid-cols-3 gap-3 text-center">
                <div className="bg-white rounded-lg border border-slate-200 p-3">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Code</div>
                  <div className="mt-1 font-mono font-black text-brand text-base flex items-center justify-center gap-1" data-testid={`amb-code-${u.id}`}>
                    {u.referral_code}
                    <button onClick={() => copy(u.referral_code)} className="text-slate-400 hover:text-brand"><Copy size={11} /></button>
                  </div>
                  <button onClick={() => updateCode(u)} className="mt-1 text-[10px] text-brand hover:underline inline-flex items-center gap-1"><RefreshCw size={9} /> Change</button>
                </div>
                <div className="bg-white rounded-lg border border-slate-200 p-3">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Wallet</div>
                  <div className="mt-1 font-black text-emerald-700 text-base flex items-center justify-center" data-testid={`amb-balance-${u.id}`}>
                    <IndianRupee size={13} />{Number(u.ambassador_wallet_balance || 0).toFixed(0)}
                  </div>
                  <div className="text-[10px] text-slate-400 mt-0.5">payable</div>
                </div>
                <div className="bg-white rounded-lg border border-slate-200 p-3">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Earned</div>
                  <div className="mt-1 font-black text-slate-700 text-base flex items-center justify-center">
                    <IndianRupee size={13} />{Number(u.ambassador_total_earned || 0).toFixed(0)}
                  </div>
                  <div className="text-[10px] text-slate-400 mt-0.5">lifetime</div>
                </div>
              </div>

              <button
                onClick={() => openTxns(u)}
                className="mt-4 w-full inline-flex items-center justify-center gap-2 text-sm font-bold text-brand bg-blue-50 hover:bg-blue-100 rounded-lg py-2"
                data-testid={`amb-txn-${u.id}`}
              >
                <Receipt size={14} /> View Transactions &amp; Pay Out
              </button>
            </div>
          );
        })}
      </div>

      {/* Create modal */}
      <Dialog open={createOpen} onOpenChange={(o) => !o && setCreateOpen(false)}>
        <DialogContent className="max-w-2xl" data-testid="create-amb-modal">
          <DialogHeader>
            <DialogTitle>Add College Ambassador</DialogTitle>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Full Name *">
              <input value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} className="inp" data-testid="amb-name" />
            </Field>
            <Field label="Email *">
              <input type="email" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} className="inp" data-testid="amb-email" />
            </Field>
            <Field label="Initial Password *">
              <input value={createForm.password} onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} className="inp" data-testid="amb-password" />
            </Field>
            <Field label="Phone">
              <input value={createForm.phone} onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })} className="inp" data-testid="amb-phone" />
            </Field>
            <Field label="College / University">
              <input value={createForm.college} onChange={(e) => setCreateForm({ ...createForm, college: e.target.value })} className="inp" data-testid="amb-college" />
            </Field>
            <Field label="State">
              <input value={createForm.state} onChange={(e) => setCreateForm({ ...createForm, state: e.target.value })} className="inp" data-testid="amb-state" />
            </Field>
            <Field label="City">
              <input value={createForm.city} onChange={(e) => setCreateForm({ ...createForm, city: e.target.value })} className="inp" data-testid="amb-city" />
            </Field>
            <Field label="Referral Code (optional)" hint="Auto-generated if blank.">
              <input value={createForm.referral_code} onChange={(e) => setCreateForm({ ...createForm, referral_code: e.target.value.toUpperCase() })} placeholder="AMBSONAL" className="inp font-mono" data-testid="amb-code-input" />
            </Field>
            <label className="sm:col-span-2 flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={createForm.notify_email} onChange={(e) => setCreateForm({ ...createForm, notify_email: e.target.checked })} data-testid="amb-notify" />
              Email the ambassador with their login + referral code
            </label>
          </div>
          <DialogFooter>
            <button onClick={() => setCreateOpen(false)} className="px-4 py-2 text-sm font-semibold">Cancel</button>
            <button onClick={create} disabled={submitting} className="bg-[#F59E0B] hover:bg-[#D97706] text-white font-black rounded-lg px-5 py-2 text-sm disabled:opacity-60 inline-flex items-center gap-2" data-testid="amb-create-submit">
              {submitting && <Loader2 size={14} className="animate-spin" />} Create Ambassador
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transactions / Payout modal */}
      <Dialog open={!!txnUser} onOpenChange={(o) => !o && setTxnUser(null)}>
        <DialogContent className="max-w-3xl" data-testid="amb-txn-modal">
          <DialogHeader>
            <DialogTitle>{txnUser?.name} — Wallet Transactions</DialogTitle>
          </DialogHeader>
          {txnUser && (
            <>
              <div className="grid grid-cols-3 gap-3 text-center mb-4">
                <Stat label="Wallet" value={`₹${Number(txnUser.ambassador_wallet_balance || 0).toFixed(0)}`} hint="payable now" tone="emerald" />
                <Stat label="Earned" value={`₹${Number(txnUser.ambassador_total_earned || 0).toFixed(0)}`} hint="lifetime" tone="brand" />
                <Stat label="Paid Out" value={`₹${Number(txnUser.ambassador_total_paid_out || 0).toFixed(0)}`} hint="till date" tone="slate" />
              </div>

              <div className="rounded-lg border-2 border-amber-200 bg-amber-50 p-4 mb-4">
                <div className="text-xs font-black uppercase tracking-[0.2em] text-amber-700 mb-2">Record Manual Payout (Bank/UPI)</div>
                <div className="grid sm:grid-cols-3 gap-2 items-end">
                  <Field label="Amount (₹)">
                    <input type="number" value={payoutAmt} onChange={(e) => setPayoutAmt(e.target.value)} className="inp" data-testid="payout-amt" />
                  </Field>
                  <Field label="Note (e.g. UTR, UPI ref)">
                    <input value={payoutNote} onChange={(e) => setPayoutNote(e.target.value)} className="inp" placeholder="UTR/UPI ref" data-testid="payout-note" />
                  </Field>
                  <button onClick={recordPayout} className="bg-amber-600 hover:bg-amber-700 text-white font-black rounded-lg px-4 py-2 text-sm" data-testid="payout-submit">
                    Mark Paid
                  </button>
                </div>
              </div>

              <div className="rounded-lg border border-slate-200 max-h-[40vh] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left sticky top-0">
                    <tr>
                      <th className="p-2.5 font-semibold">Date</th>
                      <th className="p-2.5 font-semibold">Type</th>
                      <th className="p-2.5 font-semibold text-right">Amount</th>
                      <th className="p-2.5 font-semibold">Note / Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {txns.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-slate-400">No transactions yet.</td></tr>}
                    {txns.map((t) => (
                      <tr key={t.id} className="border-t border-slate-100" data-testid={`txn-row-${t.id}`}>
                        <td className="p-2.5 text-slate-500">{new Date(t.created_at).toLocaleString()}</td>
                        <td className="p-2.5">
                          <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${t.type === "credit" ? "bg-emerald-100 text-emerald-700" : t.type === "payout" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`}>
                            {t.type}
                          </span>
                        </td>
                        <td className={`p-2.5 text-right font-mono font-bold ${t.amount > 0 ? "text-emerald-700" : t.amount < 0 ? "text-red-600" : "text-slate-400"}`}>
                          ₹{Math.abs(Number(t.amount || 0)).toFixed(2)} {t.amount > 0 ? "+" : t.amount < 0 ? "-" : ""}
                        </td>
                        <td className="p-2.5 text-slate-600 text-xs">{t.note || (t.source_txnid ? `Txn ${t.source_txnid}` : "—")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <style>{`.inp{display:block;width:100%;padding:8px 12px;border-radius:8px;border:1px solid #cbd5e1;font-size:13px;outline:none}.inp:focus{border-color:#1E3A8A}`}</style>
    </DashboardLayout>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-600">{label}</label>
      <div className="mt-1">{children}</div>
      {hint && <div className="text-[10px] text-slate-400 mt-0.5">{hint}</div>}
    </div>
  );
}

function Stat({ label, value, hint, tone }) {
  const colors = {
    emerald: "text-emerald-700 bg-emerald-50",
    brand: "text-brand bg-blue-50",
    slate: "text-slate-700 bg-slate-50",
  }[tone] || "text-slate-700 bg-slate-50";
  return (
    <div className={`p-3 rounded-lg ${colors}`}>
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold opacity-70">{label}</div>
      <div className="mt-1 font-black text-xl">{value}</div>
      <div className="text-[10px] mt-0.5 opacity-60">{hint}</div>
    </div>
  );
}
